import React, { useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, FormControl, FormLabel, Select, Textarea, VStack, useToast, Badge, Text, Alert, AlertIcon,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { actualizarEstatusOP, TramiteOP } from '../../../../api/opTramiteApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tramite: TramiteOP;
  onSuccess: () => void;
}

const ESTATUS_OPTIONS = [
  { value: 'REGISTRADO',              label: 'Registrado',                color: 'blue',   motivo: false },
  { value: 'EN_PROCESO',              label: 'En Proceso',                color: 'cyan',   motivo: false },
  { value: 'EN_REVISION',             label: 'En Revisión',               color: 'orange', motivo: false },
  { value: 'OBSERVADO',               label: 'Observado',                 color: 'yellow', motivo: true  },
  { value: 'PENDIENTE_DOCUMENTACION', label: 'Pendiente de Documentación',color: 'purple', motivo: false },
  { value: 'PENDIENTE_PAGO',          label: 'Pendiente de Pago',         color: 'pink',   motivo: false },
  { value: 'EN_INSPECCION',           label: 'En Inspección',             color: 'teal',   motivo: false },
  { value: 'PROCEDENTE',              label: 'Procedente',                color: 'green',  motivo: false },
  { value: 'NO_PROCEDENTE',           label: 'No Procedente',             color: 'red',    motivo: true  },
  { value: 'RECHAZADO',               label: 'Rechazado',                 color: 'red',    motivo: true  },
  { value: 'CONCLUIDO',               label: 'Concluido',                 color: 'gray',   motivo: false },
  { value: 'CANCELADO',               label: 'Cancelado',                 color: 'red',    motivo: true  },
];

const ModalCambiarEstatusOP: React.FC<Props> = ({ isOpen, onClose, tramite, onSuccess }) => {
  const [nuevoEstatus, setNuevoEstatus] = useState(tramite.estatus);
  const [comentario, setComentario] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const user = useSelector((state: RootState) => state.auth.user);

  const estatusActual = ESTATUS_OPTIONS.find(e => e.value === tramite.estatus);
  const estatusNuevo = ESTATUS_OPTIONS.find(e => e.value === nuevoEstatus);
  const requiereMotivo = estatusNuevo?.motivo ?? false;
  const sinCambio = nuevoEstatus === tramite.estatus;

  const handleSubmit = async () => {
    if (sinCambio) return;
    if (requiereMotivo && !comentario.trim()) {
      toast({ title: `Se requiere motivo para "${estatusNuevo?.label}"`, status: 'warning', duration: 3000 });
      return;
    }
    setLoading(true);
    try {
      await actualizarEstatusOP(tramite.id, nuevoEstatus, comentario.trim() || undefined, user?.name);
      toast({ title: 'Estatus actualizado', status: 'success', duration: 2000 });
      onSuccess();
    } catch (err: any) {
      toast({ title: 'Error', description: err.response?.data?.error || err.message, status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">Cambiar Estatus</ModalHeader>
        <ModalCloseButton color="white" />

        <ModalBody pt={4}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm"><strong>Folio:</strong> {tramite.folio}</Text>

            <Text fontSize="sm">
              <strong>Estatus actual: </strong>
              <Badge colorScheme={estatusActual?.color || 'gray'} ml={1}>{estatusActual?.label || tramite.estatus}</Badge>
            </Text>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Nuevo Estatus</FormLabel>
              <Select value={nuevoEstatus} onChange={e => { setNuevoEstatus(e.target.value); setComentario(''); }}>
                {ESTATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
            </FormControl>

            {nuevoEstatus === 'PROCEDENTE' && (
              <Alert status="success" borderRadius="md" fontSize="sm">
                <AlertIcon />
                Al marcar como Procedente se habilitará la generación del documento oficial con QR y se registrará la vigencia.
              </Alert>
            )}

            <FormControl isRequired={requiereMotivo}>
              <FormLabel fontSize="sm">
                {requiereMotivo ? 'Motivo / Observación (obligatorio)' : 'Comentario (opcional)'}
              </FormLabel>
              <Textarea
                value={comentario}
                onChange={e => setComentario(e.target.value)}
                placeholder={requiereMotivo ? 'Describa el motivo...' : 'Observaciones adicionales...'}
                rows={3}
                borderColor={requiereMotivo && !comentario ? 'red.300' : undefined}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancelar</Button>
          <Button bg="#5b182b" color="white" _hover={{ bg: '#8b1f33' }}
            onClick={handleSubmit} isLoading={loading} isDisabled={sinCambio}>
            Guardar Cambio
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalCambiarEstatusOP;
